import { useCallback, useState } from 'react'
import { Store, Plus, RefreshCcw, XCircle, Tag, Gavel } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction, FetchParams, FetchResult } from '@/components/DataTable/types'
import { Button } from '@/components/ui/button'
import { StatusBadge } from '@/components/StatusBadge'
import { useToast } from '@/providers/UiProviders'
import { ApiError } from '@/lib/api'
import { PERMISSIONS } from '@/lib/permissions'
import { CreateListingDialog } from './CreateListingDialog'
import { CreateAuctionDialog } from './CreateAuctionDialog'
import { RepriceDialog } from './RepriceDialog'
import {
  fetchListings,
  delistListing,
  migrateRetroactive,
  type AdminGromAuktionListing,
} from './gromAuktionApi'

function formatDate(unixSeconds: number | null): string {
  return unixSeconds ? new Date(unixSeconds * 1000).toLocaleString() : '—'
}

/**
 * M1 — GromAuktion marketplace (this deployment's fragment.com clone) management. Not part of
 * the original 614-screen TZ scaffold, same reasoning as GiftsScreen ("G1" pattern) - the feature
 * didn't exist when that scaffold was generated. Backend: MyTelegram.AdminServer's
 * GromAuktionAdminController (real, live) - every write goes through the EventFlow command bus.
 * The public storefront (gromauktion.unseenwallet.com) covers browsing/buying/self-listing for
 * regular users; this screen is for operator-side management (house listings, moderation-style
 * delist/reprice, and the one-shot retroactive-grandfather migration).
 */
export function GromAuktionScreen() {
  const t = useT()
  const { toast } = useToast()
  const [addOpen, setAddOpen] = useState(false)
  const [auctionOpen, setAuctionOpen] = useState(false)
  const [repriceTarget, setRepriceTarget] = useState<AdminGromAuktionListing | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [migrating, setMigrating] = useState(false)

  const fetcher = useCallback(
    async (params: FetchParams): Promise<FetchResult<AdminGromAuktionListing>> => {
      const rows = await fetchListings(params.search)
      return { rows }
    },
    [],
  )

  async function handleDelist(row: AdminGromAuktionListing) {
    try {
      await delistListing(row.id)
      toast(t.gromauktion.delistSuccess, { tone: 'success' })
      setRefreshKey((k) => k + 1)
    } catch (e) {
      toast(e instanceof ApiError ? e.message : String(e), { tone: 'error' })
    }
  }

  async function handleMigrate() {
    setMigrating(true)
    try {
      const result = await migrateRetroactive()
      const parts: string[] = []
      if (result.created.length) parts.push(`${t.gromauktion.migrateCreated}: ${result.created.join(', ')}`)
      if (result.skipped.length) parts.push(`${t.gromauktion.migrateSkipped}: ${result.skipped.join(', ')}`)
      toast(parts.length ? parts.join(' · ') : t.gromauktion.migrateNothing, { tone: 'success' })
      setRefreshKey((k) => k + 1)
    } catch (e) {
      toast(e instanceof ApiError ? e.message : String(e), { tone: 'error' })
    } finally {
      setMigrating(false)
    }
  }

  const columns: AdminColumn<AdminGromAuktionListing>[] = [
    { key: 'username', header: t.gromauktion.username, type: 'text', minWidth: 140, render: (r) => `@${r.username}` },
    {
      key: 'seller_user_id',
      header: t.gromauktion.seller,
      type: 'text',
      width: 120,
      render: (r) => (r.seller_user_id === 0 ? t.gromauktion.theHouse : String(r.seller_user_id)),
    },
    {
      key: 'price',
      header: t.gromauktion.price,
      type: 'number',
      width: 130,
      render: (r) => (r.is_auction ? `${r.current_bid.toLocaleString()} (${t.gromauktion.auctionBadge})` : r.price.toLocaleString()),
    },
    {
      key: 'status',
      header: t.col.status,
      type: 'enum',
      width: 110,
      render: (r) => {
        const kind = r.status === 'active' ? 'active' : r.status === 'sold' ? 'approved' : 'expired'
        const label =
          r.status === 'active' ? t.gromauktion.statusActive : r.status === 'sold' ? t.gromauktion.statusSold : t.gromauktion.statusDelisted
        return <StatusBadge status={kind} label={label} />
      },
    },
    {
      key: 'is_retroactive',
      header: t.gromauktion.retroactive,
      type: 'bool',
      width: 100,
      render: (r) => (r.is_retroactive ? t.common.yes : '—'),
    },
    {
      key: 'buyer_user_id',
      header: t.gromauktion.buyer,
      type: 'text',
      width: 110,
      render: (r) => (r.buyer_user_id ? String(r.buyer_user_id) : '—'),
    },
    {
      key: 'auction_end_at',
      header: t.gromauktion.endsAt,
      type: 'date',
      width: 160,
      render: (r) => (r.is_auction && r.status === 'active' ? formatDate(r.auction_end_at) : '—'),
    },
    { key: 'created_at', header: t.gromauktion.createdAt, type: 'date', width: 160, render: (r) => formatDate(r.created_at) },
    { key: 'sold_at', header: t.gromauktion.soldAt, type: 'date', width: 160, render: (r) => formatDate(r.sold_at) },
  ]

  const rowActions: RowAction<AdminGromAuktionListing>[] = [
    {
      id: 'reprice',
      label: t.gromauktion.reprice,
      icon: <Tag size={14} />,
      permission: PERMISSIONS.USERS_MODERATE,
      visible: (r) => r.status === 'active' && !r.is_auction,
      onSelect: (r) => setRepriceTarget(r),
    },
    {
      id: 'delist',
      label: t.gromauktion.delist,
      icon: <XCircle size={14} />,
      danger: true,
      permission: PERMISSIONS.USERS_MODERATE,
      visible: (r) => r.status === 'active',
      onSelect: handleDelist,
    },
  ]

  return (
    <>
      <PageHeader
        title={t.nav.gromauktion}
        screenId="M1.01"
        verified
        actions={
          <div className="flex items-center gap-2">
            <Button variant="default" size="sm" onClick={handleMigrate} disabled={migrating}>
              <RefreshCcw size={14} />
              {t.gromauktion.runMigration}
            </Button>
            <Button variant="default" size="sm" onClick={() => setAuctionOpen(true)}>
              <Gavel size={14} />
              {t.gromauktion.createAuction}
            </Button>
            <Button variant="primary" size="sm" onClick={() => setAddOpen(true)}>
              <Plus size={14} />
              {t.gromauktion.createListing}
            </Button>
          </div>
        }
      />

      <DataTable<AdminGromAuktionListing>
        key={refreshKey}
        screenId="M1.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowActions={rowActions}
        rowId={(r) => r.id}
        exportable
        emptyState={{
          icon: <Store size={28} strokeWidth={1.5} />,
          title: t.gromauktion.emptyTitle,
          description: t.gromauktion.emptyDesc,
        }}
      />

      <CreateListingDialog open={addOpen} onClose={() => setAddOpen(false)} onCreated={() => setRefreshKey((k) => k + 1)} />
      <CreateAuctionDialog open={auctionOpen} onClose={() => setAuctionOpen(false)} onCreated={() => setRefreshKey((k) => k + 1)} />
      <RepriceDialog listing={repriceTarget} onClose={() => setRepriceTarget(null)} onSaved={() => setRefreshKey((k) => k + 1)} />
    </>
  )
}
